/*
 * The canonical mapping from backend status strings to a visual *tone* and a
 * human label. Two domains live here:
 *
 *   1. Audit verdicts — the 8 classifications a ticket can receive
 *      (`InconsistencyRow.classification` / `audit_records.classification`).
 *   2. Run states — the 17 pipeline states from the state-machine diagram,
 *      plus the `control` overlay (pausing/cancelling) the API surfaces.
 *
 * Backend strings are authoritative; we normalise case/separators and fall
 * back to a neutral tone for anything unrecognised so a new server enum value
 * renders sanely instead of crashing. This module is framework-agnostic (no
 * React/Mantine) — it is imported by both UI primitives (the StatusBadge that
 * paints these tones) and pure logic (pipeline transitions/stages, verdict
 * copy). It lives in `src/lib/` for that reason; the visual shells live in the
 * design layer.
 */

export type StatusTone =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  | "attention";

export interface StatusMeta {
  tone: StatusTone;
  label: string;
}

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function titleize(value: string): string {
  return value
    .replace(/[_\s-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ---- Audit verdicts (8) ----------------------------------------------- */

export const AUDIT_VERDICTS = [
  "verified_ok",
  "count_mismatch",
  "name_mismatch",
  "ads_disapproved",
  "adset_not_found",
  "claimed_failure_but_launched",
  "failed_confirmed",
  "unverifiable",
] as const;

export type AuditVerdict = (typeof AUDIT_VERDICTS)[number];

const VERDICT_TONE: Record<AuditVerdict, StatusTone> = {
  verified_ok: "success",
  count_mismatch: "warning",
  name_mismatch: "warning",
  ads_disapproved: "danger",
  adset_not_found: "danger",
  claimed_failure_but_launched: "attention",
  failed_confirmed: "danger",
  unverifiable: "neutral",
};

const VERDICT_LABEL: Record<AuditVerdict, string> = {
  verified_ok: "Verified OK",
  count_mismatch: "Count Mismatch",
  name_mismatch: "Name Mismatch",
  ads_disapproved: "Ads Disapproved",
  adset_not_found: "Adset Not Found",
  claimed_failure_but_launched: "Claimed Failure / Launched",
  failed_confirmed: "Failed (Confirmed)",
  unverifiable: "Unverifiable",
};

/* ---- Run states (18) + control overlay -------------------------------- */

export const RUN_STATES = [
  "queued",
  "blocked",
  "prepping",
  "prepped",
  "uploading",
  "uploaded",
  "awaiting_confirm",
  "finalizing",
  "finalized",
  "auditing",
  "audited_clean",
  "recheck_pending",
  "audited_with_discrepancies",
  "recovering",
  "bookkeeping",
  "completed",
  "drafted",
  "paused",
  "needs_reauth",
  "aborted",
  "failed",
] as const;

export type RunState = (typeof RUN_STATES)[number];

const RUN_STATE_TONE: Record<RunState, StatusTone> = {
  queued: "neutral",
  blocked: "neutral",
  prepping: "info",
  prepped: "info",
  uploading: "info",
  uploaded: "info",
  awaiting_confirm: "attention",
  finalizing: "info",
  finalized: "info",
  auditing: "info",
  audited_clean: "success",
  recheck_pending: "warning",
  audited_with_discrepancies: "warning",
  recovering: "warning",
  bookkeeping: "info",
  completed: "success",
  drafted: "info",
  paused: "warning",
  needs_reauth: "attention",
  aborted: "danger",
  failed: "danger",
};

const RUN_STATE_LABEL: Partial<Record<RunState, string>> = {
  prepping: "Preparing",
  prepped: "Prepared",
  audited_clean: "Audited — Clean",
  audited_with_discrepancies: "Audited — Discrepancies",
  awaiting_confirm: "Awaiting Confirm",
  recheck_pending: "Recheck Pending",
  needs_reauth: "Needs Re-auth",
  drafted: "Drafted",
};

/* ---- Resolvers -------------------------------------------------------- */

/** Tone + label for an audit verdict string (case/separator tolerant). */
export function auditVerdictMeta(classification: string): StatusMeta {
  const key = normalize(classification) as AuditVerdict;
  if (key in VERDICT_TONE) {
    return { tone: VERDICT_TONE[key], label: VERDICT_LABEL[key] };
  }
  return { tone: "neutral", label: titleize(classification) };
}

/** Tone + label for a run status string (case/separator tolerant). */
export function runStatusMeta(status: string): StatusMeta {
  const key = normalize(status) as RunState;
  if (key in RUN_STATE_TONE) {
    return { tone: RUN_STATE_TONE[key], label: RUN_STATE_LABEL[key] ?? titleize(key) };
  }
  return { tone: "neutral", label: titleize(status) };
}

/**
 * The `control` field overlays the run status (none/pausing/paused/cancelling).
 * Returns `null` for `none`/empty so callers can skip rendering an overlay
 * badge when there is no pending control transition.
 */
export function runControlMeta(control: string): StatusMeta | null {
  const key = normalize(control);
  switch (key) {
    case "":
    case "none":
      return null;
    case "pausing":
      return { tone: "warning", label: "Pausing…" };
    case "paused":
      return { tone: "warning", label: "Paused" };
    case "cancelling":
      return { tone: "danger", label: "Cancelling…" };
    default:
      return { tone: "neutral", label: titleize(control) };
  }
}

/** True for run states from which no further pipeline transition occurs. */
export function isTerminalRunState(status: string): boolean {
  const key = normalize(status);
  return (
    key === "completed" ||
    key === "drafted" ||
    key === "aborted" ||
    key === "failed"
  );
}
